import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTitle } from "../hooks/useTitle";
import { useUser } from "../context/UserContext";
import axios from "axios";
import { API_URL } from "../config";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaFutbol, FaHistory, FaCheckCircle, FaTimesCircle, FaClock, FaTrophy, FaArrowLeft, FaUndo, FaExclamationTriangle } from "react-icons/fa";
import AdSense from "react-adsense";
import { showAd } from "../components/MonetagHelper";
import { ShowFootballAdButton } from "../components/MoneTag";

function FootballPredictionGame() {
  useTitle("Football Prediction");
  const { userData, setUserData, APIKey } = useUser();
  const navigate = useNavigate();
  
  // Safe accessor for initData
  const initData = window.Telegram?.WebApp?.initData || "";
  
  const [activeTab, setActiveTab] = useState("upcoming");
  const [matches, setMatches] = useState([]);
  const [myBets, setMyBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeague, setSelectedLeague] = useState("All");
  
  // Betting modal state
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedBet, setSelectedBet] = useState(null); // { type: '1X2', prediction: 'home', multiplier: 1.5 }
  const [betAmount, setBetAmount] = useState(10);
  const [isPlacingBet, setIsPlacingBet] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === "upcoming") {
        const res = await axios.get(`${API_URL}/api/football/matches`, {
          params: { initData },
          headers: { "x-api-key": APIKey }
        });
        setMatches(res.data.matches || []);
      } else {
        const res = await axios.get(`${API_URL}/api/football/my-bets`, {
          params: { initData },
          headers: { "x-api-key": APIKey }
        });
        setMyBets(res.data.bets || []);
      }
    } catch (err) {
      console.error("Failed to fetch football data", err);
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenBetModal = (match, type, prediction, multiplier) => {
    // Check if betting is closed (2 hours before)
    const twoHoursBefore = new Date(new Date(match.startTime).getTime() - 2 * 60 * 60 * 1000);
    if (new Date() >= twoHoursBefore) {
      toast.error("Betting is closed for this match.");
      return;
    }
    setSelectedMatch(match);
    setSelectedBet({ type, prediction, multiplier });
    setBetAmount(10);
  };

  const placeBet = async () => {
    if (userData.user.points < betAmount) {
      toast.error("Not enough gems!");
      return;
    }
    setIsPlacingBet(true);
    try {
      const res = await axios.post(`${API_URL}/api/football/bet`, {
        matchId: selectedMatch.matchId,
        betType: selectedBet.type,
        prediction: selectedBet.prediction,
        betAmount: betAmount
      }, {
        params: { initData },
        headers: { "x-api-key": APIKey }
      });
      
      setUserData({ ...userData, user: { ...userData.user, points: res.data.user.points } });
      setMatches(prev => prev.map(m => {
        if (m.matchId === selectedMatch.matchId) {
          const newTypes = m.hasBetTypes ? [...m.hasBetTypes] : [];
          if (!newTypes.includes(selectedBet.type)) newTypes.push(selectedBet.type);
          return { ...m, hasBetTypes: newTypes };
        }
        return m;
      }));
      toast.success("Bet placed successfully!");
      setSelectedMatch(null);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to place bet.");
    } finally {
      setIsPlacingBet(false);
    }
  };

  const claimReward = async (betId) => {
    // Show interstitial ad before claiming reward
    await showAd();

    try {
      const res = await axios.post(`${API_URL}/api/football/claim`, { betId }, {
        params: { initData },
        headers: { "x-api-key": APIKey }
      });
      setUserData({ ...userData, user: { ...userData.user, points: res.data.user.points } });
      toast.success(`Claimed ${res.data.bet.potentialWin} gems! 🎉`);
      fetchData(); // refresh bets
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to claim reward.");
    }
  };

  const getPredictionLabel = (type, pred, match) => {
    if (!match) return pred; // Fallback if match was deleted
    
    if (type === '1X2') {
      if (pred === 'home') return match.homeTeam?.name || 'Home';
      if (pred === 'away') return match.awayTeam?.name || 'Away';
      return "Draw";
    } else if (type === 'OU') {
      return `${pred === 'over' ? 'Over' : 'Under'} ${match.odds?.overUnderThreshold || ''}`;
    } else {
      return pred === 'yes' ? 'Yes' : 'No';
    }
  };

  return (
    <div className="bg-slate-50 dark:bg-[#0a0f1a] min-h-screen text-slate-900 dark:text-white pt-20 pb-28">
      <div className="container mx-auto px-4 max-w-lg relative">
        
        {/* Header */}
        <div className="flex items-center mb-6">
          <button onClick={() => navigate('/game')} className="p-2 bg-slate-200 dark:bg-slate-800 rounded-full mr-4">
            <FaArrowLeft className="text-slate-600 dark:text-slate-300" />
          </button>
          <div>
            <h2 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-green-500">
              Football Prediction
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">Bet on real matches and win gems!</p>
          </div>
        </div>

        {/* Warning Banner */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-xl p-3 mb-6 flex gap-3 items-start shadow-sm">
          <FaExclamationTriangle className="text-amber-500 shrink-0 mt-0.5 text-sm" />
          <p className="text-[11px] sm:text-xs text-amber-700 dark:text-amber-300 leading-relaxed font-bold">
            Biscope In-game currency ဖြစ်သည့် Gem ဖြင့်သာ အပျော်ဆော့ကစားနိုင်မည်ဖြစ်ပြီး<br className="hidden sm:block" /> ငွေကြေးဖြင့် အလျော်အစား လုံးဝ (လုံးဝ) မပြုလုပ်ပါ။
          </p>
        </div>

        {userData.user?.points < 101 && (
          <div className="mb-6 animate-fade-in">
            <div className="text-center text-rose-500 text-sm font-bold mb-2 flex items-center justify-center gap-1">
              <FaExclamationTriangle /> You are low in gems! Watch ads to get more.
            </div>
            <ShowFootballAdButton
              userId={userData.user?.userId || userData.userId}
              webApp={initData}
              APIKey={APIKey}
              onPointsUpdate={(earnedPoints) => {
                setUserData({ ...userData, user: { ...userData.user, points: userData.user.points + earnedPoints } });
              }}
            />
          </div>
        )}

        {/* Tabs */}
        <div className="flex bg-slate-200 dark:bg-slate-800 rounded-full p-1 mb-6">
          <button
            onClick={() => setActiveTab("upcoming")}
            className={`flex-1 py-2 text-sm font-bold rounded-full transition-all flex items-center justify-center gap-1 sm:gap-2 ${
              activeTab === "upcoming" ? "bg-white dark:bg-slate-700 shadow text-emerald-500" : "text-slate-500 dark:text-slate-400"
            }`}
          >
            <FaFutbol /> <span className="hidden sm:inline">Matches</span><span className="sm:hidden">Match</span>
          </button>
          <button
            onClick={() => setActiveTab("current")}
            className={`flex-1 py-2 text-sm font-bold rounded-full transition-all flex items-center justify-center gap-1 sm:gap-2 ${
              activeTab === "current" ? "bg-white dark:bg-slate-700 shadow text-emerald-500" : "text-slate-500 dark:text-slate-400"
            }`}
          >
            <FaClock /> Current
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`flex-1 py-2 text-sm font-bold rounded-full transition-all flex items-center justify-center gap-1 sm:gap-2 ${
              activeTab === "history" ? "bg-white dark:bg-slate-700 shadow text-emerald-500" : "text-slate-500 dark:text-slate-400"
            }`}
          >
            <FaHistory /> History
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500"></div>
          </div>
        ) : activeTab === "upcoming" ? (
          <div className="space-y-4">
            {matches.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-2 mb-2 scrollbar-hide">
                {["All", ...new Set(matches.map(m => m.league))].map(league => (
                  <button
                    key={league}
                    onClick={() => setSelectedLeague(league)}
                    className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                      selectedLeague === league 
                        ? 'bg-emerald-500 text-white shadow' 
                        : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                    }`}
                  >
                    {league}
                  </button>
                ))}
              </div>
            )}

            {matches.filter(m => selectedLeague === "All" || m.league === selectedLeague).length === 0 ? (
              <div className="text-center py-10 text-slate-500">No upcoming matches available.</div>
            ) : (
              matches.filter(m => selectedLeague === "All" || m.league === selectedLeague).map((match, index) => {
                const isClosed = new Date() >= new Date(new Date(match.startTime).getTime() - 2 * 60 * 60 * 1000);
                
                return (
                  <React.Fragment key={match._id}>
                    {index > 0 && index % 3 === 0 && (
                      <div className="col-span-full my-4 flex flex-col items-center overflow-hidden rounded-xl bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-slate-800">
                        <AdSense.Google
                          client="ca-pub-4252052358641832"
                          slot="4385377268"
                          style={{ display: "block", width: "100%" }}
                          format="auto"
                          responsive="true"
                          layoutKey="-gw-1+2a-9x+5c"
                        />
                      </div>
                    )}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-slate-200 dark:border-slate-800 relative overflow-hidden"
                    >
                    {/* Match Header */}
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-xs font-bold text-slate-500 uppercase">{match.league}</span>
                      <span className={`text-xs font-bold px-2 py-1 rounded ${isClosed ? 'bg-red-100 text-red-600 dark:bg-red-900/30' : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30'}`}>
                        {isClosed ? 'CLOSED' : new Date(match.startTime).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>

                    <div className="flex justify-between items-center mb-4 px-2">
                      <div className="flex flex-col items-center flex-1">
                        {match.homeTeam.logo ? (
                          <img src={match.homeTeam.logo} alt={match.homeTeam.name} className="w-12 h-12 object-contain mb-1" />
                        ) : <div className="w-12 h-12 bg-slate-200 dark:bg-slate-800 rounded-full mb-1 flex items-center justify-center"><FaFutbol className="text-slate-400"/></div>}
                        <span className="text-xs font-bold text-center">{match.homeTeam.name}</span>
                      </div>
                      <div className="px-4 font-black text-slate-300 dark:text-slate-600 text-xl">VS</div>
                      <div className="flex flex-col items-center flex-1">
                        {match.awayTeam.logo ? (
                          <img src={match.awayTeam.logo} alt={match.awayTeam.name} className="w-12 h-12 object-contain mb-1" />
                        ) : <div className="w-12 h-12 bg-slate-200 dark:bg-slate-800 rounded-full mb-1 flex items-center justify-center"><FaFutbol className="text-slate-400"/></div>}
                        <span className="text-xs font-bold text-center">{match.awayTeam.name}</span>
                      </div>
                    </div>

                    {/* Odds 1X2 */}
                    <div className="mb-2 relative">
                      {match.hasBetTypes?.includes('1X2') && (
                        <div className="absolute inset-0 z-10 bg-white/70 dark:bg-slate-900/70 backdrop-blur-[2px] flex flex-col items-center justify-center rounded-xl">
                          <FaCheckCircle className="text-2xl text-emerald-500 mb-1 drop-shadow-md" />
                          <span className="font-black text-slate-800 dark:text-slate-100 text-sm drop-shadow-md">Bet Placed</span>
                        </div>
                      )}
                      <div className="text-[10px] uppercase text-slate-400 font-bold mb-1 ml-1">Match Winner</div>
                      <div className="flex gap-2">
                        <button onClick={() => handleOpenBetModal(match, '1X2', 'home', match.odds.homeWin)} disabled={isClosed} className="flex-1 bg-slate-100 dark:bg-slate-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border border-transparent hover:border-emerald-500 rounded-xl py-2 flex flex-col items-center transition-colors disabled:opacity-50 disabled:hover:border-transparent">
                          <span className="text-xs text-slate-500">1</span>
                          <span className="font-bold">{match.odds.homeWin}x</span>
                        </button>
                        <button onClick={() => handleOpenBetModal(match, '1X2', 'draw', match.odds.draw)} disabled={isClosed} className="flex-1 bg-slate-100 dark:bg-slate-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border border-transparent hover:border-emerald-500 rounded-xl py-2 flex flex-col items-center transition-colors disabled:opacity-50 disabled:hover:border-transparent">
                          <span className="text-xs text-slate-500">X</span>
                          <span className="font-bold">{match.odds.draw}x</span>
                        </button>
                        <button onClick={() => handleOpenBetModal(match, '1X2', 'away', match.odds.awayWin)} disabled={isClosed} className="flex-1 bg-slate-100 dark:bg-slate-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border border-transparent hover:border-emerald-500 rounded-xl py-2 flex flex-col items-center transition-colors disabled:opacity-50 disabled:hover:border-transparent">
                          <span className="text-xs text-slate-500">2</span>
                          <span className="font-bold">{match.odds.awayWin}x</span>
                        </button>
                      </div>
                    </div>

                    {/* Odds OU */}
                    {match.odds.over > 0 && match.odds.under > 0 && (
                      <div className="relative mb-2">
                        {match.hasBetTypes?.includes('OU') && (
                          <div className="absolute inset-0 z-10 bg-white/70 dark:bg-slate-900/70 backdrop-blur-[2px] flex flex-col items-center justify-center rounded-xl">
                            <FaCheckCircle className="text-2xl text-emerald-500 mb-1 drop-shadow-md" />
                            <span className="font-black text-slate-800 dark:text-slate-100 text-sm drop-shadow-md">Bet Placed</span>
                          </div>
                        )}
                        <div className="text-[10px] uppercase text-slate-400 font-bold mb-1 ml-1">Total Goals</div>
                        <div className="flex gap-2">
                          <button onClick={() => handleOpenBetModal(match, 'OU', 'over', match.odds.over)} disabled={isClosed} className="flex-1 bg-slate-100 dark:bg-slate-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border border-transparent hover:border-emerald-500 rounded-xl py-2 flex flex-col items-center transition-colors disabled:opacity-50 disabled:hover:border-transparent">
                            <span className="text-xs text-slate-500">Over {match.odds.overUnderThreshold}</span>
                            <span className="font-bold">{match.odds.over}x</span>
                          </button>
                          <button onClick={() => handleOpenBetModal(match, 'OU', 'under', match.odds.under)} disabled={isClosed} className="flex-1 bg-slate-100 dark:bg-slate-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border border-transparent hover:border-emerald-500 rounded-xl py-2 flex flex-col items-center transition-colors disabled:opacity-50 disabled:hover:border-transparent">
                            <span className="text-xs text-slate-500">Under {match.odds.overUnderThreshold}</span>
                            <span className="font-bold">{match.odds.under}x</span>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Odds BTTS */}
                    {match.odds.bttsYes > 0 && match.odds.bttsNo > 0 && (
                      <div className="relative">
                        {match.hasBetTypes?.includes('BTTS') && (
                          <div className="absolute inset-0 z-10 bg-white/70 dark:bg-slate-900/70 backdrop-blur-[2px] flex flex-col items-center justify-center rounded-xl">
                            <FaCheckCircle className="text-2xl text-emerald-500 mb-1 drop-shadow-md" />
                            <span className="font-black text-slate-800 dark:text-slate-100 text-sm drop-shadow-md">Bet Placed</span>
                          </div>
                        )}
                        <div className="text-[10px] uppercase text-slate-400 font-bold mb-1 ml-1">Both Teams To Score</div>
                        <div className="flex gap-2">
                          <button onClick={() => handleOpenBetModal(match, 'BTTS', 'yes', match.odds.bttsYes)} disabled={isClosed} className="flex-1 bg-slate-100 dark:bg-slate-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border border-transparent hover:border-emerald-500 rounded-xl py-2 flex flex-col items-center transition-colors disabled:opacity-50 disabled:hover:border-transparent">
                            <span className="text-xs text-slate-500">Yes</span>
                            <span className="font-bold">{match.odds.bttsYes}x</span>
                          </button>
                          <button onClick={() => handleOpenBetModal(match, 'BTTS', 'no', match.odds.bttsNo)} disabled={isClosed} className="flex-1 bg-slate-100 dark:bg-slate-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border border-transparent hover:border-emerald-500 rounded-xl py-2 flex flex-col items-center transition-colors disabled:opacity-50 disabled:hover:border-transparent">
                            <span className="text-xs text-slate-500">No</span>
                            <span className="font-bold">{match.odds.bttsNo}x</span>
                          </button>
                        </div>
                      </div>
                    )}

                  </motion.div>
                  </React.Fragment>
                );
              })
            )}
          </div>
        ) : (
          <div className="space-y-4">
            { (() => {
                const isCurrent = activeTab === "current";
                const filteredBets = myBets.filter(b => 
                  isCurrent ? (b.status === 'pending' || b.status === 'won') 
                            : (b.status === 'lost' || b.status === 'claimed' || b.status === 'refunded')
                );
                
                if (filteredBets.length === 0) {
                  return <div className="text-center py-10 text-slate-500">
                    {isCurrent ? "You don't have any current bets." : "You don't have any bet history."}
                  </div>;
                }

                return filteredBets.map((bet, index) => (
                  <React.Fragment key={bet._id}>
                    {index > 0 && index % 3 === 0 && (
                      <div className="col-span-full my-4 flex flex-col items-center overflow-hidden rounded-xl bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-slate-800">
                        <AdSense.Google
                          client="ca-pub-4252052358641832"
                          slot="4385377268"
                          style={{ display: "block", width: "100%" }}
                          format="auto"
                          responsive="true"
                          layoutKey="-gw-1+2a-9x+5c"
                        />
                      </div>
                    )}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-slate-200 dark:border-slate-800"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="text-sm font-bold">{bet.match ? `${bet.match.homeTeam.name} vs ${bet.match.awayTeam.name}` : `Match ID: ${bet.matchId}`}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          {bet.betType === '1X2' ? 'Match Winner' : (bet.betType === 'OU' ? 'Total Goals' : 'BTTS')} • <span className="font-bold text-emerald-500">{getPredictionLabel(bet.betType, bet.prediction, bet.match)}</span> • {bet.multiplier}x
                        </div>
                        {bet.match?.actualResult && (
                          <div className="text-xs font-bold mt-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 inline-block px-2 py-0.5 rounded">
                            Final Score: {bet.match.actualResult.homeGoals} - {bet.match.actualResult.awayGoals}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-500">Bet Amount</div>
                        <div className="font-bold text-sm">{bet.betAmount} 💎</div>
                      </div>
                    </div>

                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 flex justify-between items-center mt-3">
                      <div className="flex items-center gap-2">
                        {bet.status === 'pending' && <><FaClock className="text-amber-500"/> <span className="text-amber-500 text-sm font-bold">Pending</span></>}
                        {bet.status === 'won' && <><FaCheckCircle className="text-emerald-500"/> <span className="text-emerald-500 text-sm font-bold">Won</span></>}
                        {bet.status === 'lost' && <><FaTimesCircle className="text-rose-500"/> <span className="text-rose-500 text-sm font-bold">Lost</span></>}
                        {bet.status === 'claimed' && <><FaTrophy className="text-fuchsia-500"/> <span className="text-fuchsia-500 text-sm font-bold">Claimed</span></>}
                        {bet.status === 'refunded' && <><FaUndo className="text-slate-500"/> <span className="text-slate-500 text-sm font-bold">Refunded</span></>}
                      </div>
                      
                      {bet.status === 'won' ? (
                        <button onClick={() => claimReward(bet._id)} className="bg-emerald-500 text-white text-xs font-bold px-4 py-2 rounded-full shadow hover:bg-emerald-600 transition-colors">
                          Claim {bet.potentialWin} 💎
                        </button>
                      ) : (
                        <div className="text-sm font-bold">
                          {bet.status === 'lost' ? '0 💎' : bet.status === 'refunded' ? `+${bet.betAmount} 💎 (Refunded)` : `Est. Win: ${bet.potentialWin} 💎`}
                        </div>
                      )}
                    </div>
                  </motion.div>
                  </React.Fragment>
                ));
            })() }
          </div>
        )}

      </div>

      {/* Bet Modal */}
      <AnimatePresence>
        {selectedMatch && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-t-3xl sm:rounded-3xl p-6 pb-28 sm:pb-8 border border-slate-200 dark:border-slate-700"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black">Place Bet</h3>
                <button onClick={() => setSelectedMatch(null)} className="p-2 bg-slate-100 dark:bg-slate-800 rounded-full">
                  <FaTimesCircle className="text-slate-500" />
                </button>
              </div>

              <div className="text-center mb-6">
                <div className="text-sm text-slate-500 mb-1">{selectedMatch.homeTeam.name} vs {selectedMatch.awayTeam.name}</div>
                <div className="text-xs text-emerald-600 dark:text-emerald-400 font-bold mb-1 uppercase tracking-wider">
                  {selectedBet.type === '1X2' ? 'Match Winner' : (selectedBet.type === 'OU' ? 'Total Goals' : 'Both Teams To Score')}
                </div>
                <div className="text-lg font-bold">
                  {getPredictionLabel(selectedBet.type, selectedBet.prediction, selectedMatch)} <span className="text-emerald-500">({selectedBet.multiplier}x)</span>
                </div>
              </div>

              <div className="mb-6">
                <label className="text-xs font-bold text-slate-500 mb-2 block uppercase">Bet Amount (Gems)</label>
                <div className="flex gap-2">
                  {[10, 20, 50, 100].map(amt => (
                    <button
                      key={amt}
                      onClick={() => setBetAmount(amt)}
                      className={`flex-1 py-3 rounded-xl font-bold transition-all ${
                        betAmount === amt 
                          ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30' 
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                      }`}
                    >
                      {amt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 mb-4 flex justify-between items-center border border-emerald-100 dark:border-emerald-800/30">
                <span className="text-sm font-bold text-slate-600 dark:text-slate-300">Potential Win</span>
                <span className="text-xl font-black text-emerald-500">{Math.floor(betAmount * selectedBet.multiplier)} 💎</span>
              </div>

              <div className="mb-6 flex justify-center w-full overflow-hidden">
                <div style={{ width: "320px", height: "100px" }}>
                  <AdSense.Google
                    client="ca-pub-4252052358641832"
                    slot="4385377268"
                    style={{ display: "inline-block", width: "320px", height: "100px" }}
                    format=""
                  />
                </div>
              </div>

              <button
                onClick={placeBet}
                disabled={isPlacingBet}
                className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white font-black py-4 rounded-2xl shadow-xl shadow-emerald-500/20 transition-transform active:scale-95 disabled:opacity-70 flex justify-center items-center"
              >
                {isPlacingBet ? <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div> : "Confirm Bet"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default FootballPredictionGame;
